import React, { useEffect, useState, useRef } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { content } from '../mockData';

const ExecutiveSummarySection = () => {
  const { language } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const executiveContent = content[language].executive;

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section 
      id="executive" 
      ref={sectionRef}
      className="section"
      style={{ background: '#ffffff', paddingTop: '100px' }}
    >
      <div className="container">
        {/* Header */}
        <div className={`text-center mb-48 fade-in-up ${isVisible ? 'visible' : ''}`}>
          <h2>{executiveContent.title}</h2>
          <div className="divider" />
        </div>

        {/* Intro */}
        <div 
          className={`fade-in-up ${isVisible ? 'visible' : ''}`}
          style={{
            transitionDelay: '0.2s',
            maxWidth: '900px',
            margin: '0 auto 64px',
            textAlign: 'center'
          }}
        >
          <p style={{ 
            fontSize: '1.0625rem',
            lineHeight: 1.8,
            color: 'var(--charcoal)'
          }}>
            {executiveContent.intro}
          </p>
        </div>

        {/* Key Points Grid */}
        <div 
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '24px',
            marginBottom: '64px'
          }}
        >
          {executiveContent.keyPoints.map((point, index) => (
            <div
              key={point.label}
              className={`fade-in-up ${isVisible ? 'visible' : ''}`}
              style={{
                transitionDelay: `${0.3 + index * 0.1}s`,
                padding: '32px',
                background: 'var(--ivory)',
                border: '1px solid rgba(0,0,0,0.08)',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'var(--olive)';
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.08)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'rgba(0,0,0,0.08)';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div style={{
                fontSize: '0.875rem',
                fontWeight: 600,
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
                color: 'var(--olive)',
                marginBottom: '12px'
              }}>
                {point.label}
              </div>
              <div style={{
                fontSize: '0.9375rem',
                lineHeight: 1.7,
                color: 'var(--charcoal)'
              }}>
                {point.value}
              </div>
            </div>
          ))}
        </div>

        {/* Note */}
        <div 
          className={`fade-in-up ${isVisible ? 'visible' : ''}`}
          style={{
            transitionDelay: '1.0s',
            maxWidth: '900px',
            margin: '0 auto',
            padding: '32px',
            background: '#f9f9f9',
            borderLeft: '4px solid var(--olive)'
          }}
        >
          <p style={{ 
            fontSize: '0.9375rem',
            lineHeight: 1.8,
            color: '#5a5a5a',
            fontStyle: 'italic',
            margin: 0
          }}>
            {executiveContent.note}
          </p>
        </div>
      </div>
    </section>
  );
};

export default ExecutiveSummarySection;
